import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  CardsColumn,
  DotButton,
  DotColumn,
  NavigationWrapper
} from "./ActivityDotNavigation.styles";
import type { ActivityDotNavigationProps, ActivityItem } from "./ActivityDotNavigation.types";
import { CourseActivityCard } from "./CourseActivityCard.component";
import { useNavigate } from "../../services";

export function ActivityDotNavigation({
  activities,
  teachers,
  initialActiveLoId
}: ActivityDotNavigationProps) {
  const [activeIndex, setActiveIndex] = useState(() => {
    if (initialActiveLoId != null) {
      const initialIndex = activities?.findIndex((activity) => activity.id === initialActiveLoId) ?? -1;
      if (initialIndex >= 0) return initialIndex;
    }
    return 0;
  });

  const navigate = useNavigate();

  const syncUrlToActivity = useCallback((activity?: ActivityItem) => {
    if (!activity?.id || !activity.learningObjectTypology) return;
    const detailLink = navigate.getDetailLink({
      courseId: activity.courseId ?? activity.parentId ?? undefined,
      id: activity.id,
      isForYou: window.location.href.includes("per-te"),
      learningObjectTypology: activity.learningObjectTypology,
      rootId: activity.rootId ?? undefined
    });
    if (window.location.pathname === detailLink) return;
    navigate(detailLink, { replace: true, state: { preserveScroll: true } });
  }, [navigate]);

  const handleDotClick = useCallback((index: number) => {
    setActiveIndex(index);
    syncUrlToActivity(activities?.[index]);
  }, [activities, syncUrlToActivity]);

  
  useEffect(() => {
    syncUrlToActivity(activities?.[activeIndex]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if card index is above the active index, the card must be shown
  //const isCardVisible = (index: number) => index >= activeIndex; // nascondo le card precedenti a quella attiva
  const isCardVisible = () => true;
  const isCardActive = (index: number) => index === activeIndex;

  return (
    <NavigationWrapper>
      <DotColumn tabIndex={ -1 } role="tablist">
        { activities?.map((activity, index) => (
          <Stack
            key={ index }
            tabIndex={ -1 }
            aria-selected={ isCardActive(index) }
            role="tab"
            onClick={ () => handleDotClick(index) }
            sx={ {
              alignItems: "center",
              cursor: "pointer",
              height: "1.25rem",
              justifyContent: "center",
              width: "1.5rem"
            } }
          >
            <DotButton
              tabIndex={ -1 }
              $active={ isCardActive(index) }
              $isComplete={ activity.status === "C" }
            />
          </Stack>
        )) }
      </DotColumn>

      <CardsColumn
        id="cards-column"
      >
        { activities?.map((activity, index) => (
          <Box
            key={ `${index}-${activity.title}` }
            sx={ {
              height: 0,
              marginTop: 0,
              minHeight: 0,
              opacity: 0,
              overflow: "hidden",
              pointerEvents: "none",
              transition: "all 0.35s linear",
              ...isCardVisible() && {
                height: "unset",
                marginTop: "0.5rem",
                minHeight: "4.1875rem",
                opacity: 1,
                pointerEvents: "auto"
              },
              "& .card-bottom-box": {
                opacity: 0,
                overflow: "hidden",
                transition: "all 0.2s linear",
                ...isCardActive(index) && {
                  opacity: 1
                }
              },
              "& .course-activity-card": {
                gap: "0.75rem",
                transition: "all 0.35s linear",
                ...isCardActive(index) && {
                  gap: "1rem",
                  height: "unset"

                }
              }
            } }
          >
            <CourseActivityCard
              { ...activity }
              activity={ activity }
              teachers={ teachers }
              index={ index + 1 }
              realIndex={ index }
              isActive={ isCardActive(index) }
              handleActivityClick={ (i) => handleDotClick(i as number) }
              madeAttempts={ undefined }
            />
          </Box>
        )) }
      </CardsColumn>
    </NavigationWrapper>
  );
}
